// components/gamification/GamificationStats.js
"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Coins, Flame, Plus,
  BookOpen, Lock, Unlock, Check, ChevronUp, ChevronDown, User, Filter,
  Folder, Menu, Settings, X, ArrowLeft,
  Info, CheckCircle2, HelpCircle, ClipboardList, Zap, ChevronLeft,
  ChevronRight, Search, FileText, Download, Eye, Clock, Edit, Award,
  Camera, Mic, Monitor, AlertTriangle, LayoutGrid,
  Video, Gamepad2, Sparkles
} from "lucide-react";
import { useFuncion } from "../../context/FuncionContext";
import { getEcuadorYMD } from "../../utils/ecuadorTime";

/* ========== CSS embebido (incluye animaciones) ========== */
const CSS = `
:root{
  --stat-border:#e5e7eb;
  --stat-bg:#ffffff;
  --stat-muted:#6b7280;
  --stat-brand:#0f5fb8;
  --coin-bg:#f1f5ff;

  --shadow-1:0 6px 24px rgba(0,0,0,.06);
  --shadow-card:0 10px 22px rgba(0,0,0,.08);

  --ring-track:#f1f5f9;

  --fire-on-a:#ff7a18;
  --fire-on-b:#ffb31c;
  --fire-on-c:#ffd56a;

  --fire-dim-a:#9ca3af;
  --fire-dim-b:#d1d5db;
  --fire-dim-c:#e5e7eb;
}
  
/* ===== INDICADOR DE COMPRA EN COMODINES (MEJORADO V2) ===== */
.purchase-indicator-btn {
  position: absolute;
  top: 50%;
  right: -12px;
  transform: translateY(-50%);
  width: 38px;
  height: 38px;
  background: linear-gradient(135deg, #10b981, #059669);
  border: 3px solid #ffffff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 8px 20px rgba(16, 185, 129, 0.45), 
              0 0 0 4px rgba(16, 185, 129, 0.12);
  animation: purchasePulse 2.2s ease-in-out infinite;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 15;
  outline: none;
}

.purchase-indicator-btn:hover {
  transform: translateY(-50%) scale(1.12);
  box-shadow: 0 10px 26px rgba(16, 185, 129, 0.6), 
              0 0 0 6px rgba(16, 185, 129, 0.2);
  background: linear-gradient(135deg, #059669, #047857);
}

.purchase-indicator-btn:active {
  transform: translateY(-50%) scale(1.02);
}

.purchase-indicator-btn svg {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

@keyframes purchasePulse {
  0%, 100% {
    box-shadow: 0 8px 20px rgba(16, 185, 129, 0.45), 
                0 0 0 4px rgba(16, 185, 129, 0.12);
  }
  50% {
    box-shadow: 0 10px 26px rgba(16, 185, 129, 0.65), 
                0 0 0 8px rgba(16, 185, 129, 0.25),
                0 0 32px rgba(16, 185, 129, 0.35);
  }
}

/* Responsive: hacer el botón más pequeño en móvil */
@media(max-width: 520px) {
  .purchase-indicator-btn {
    width: 34px;
    height: 34px;
    right: -10px;
    border-width: 2.5px;
  }
}

/* =======================================================
   WRAPPER (compacto y alineado)
   ======================================================= */
.gami-wrap{
  display:flex;
  flex-wrap:nowrap;
  align-items:center;
  justify-content:center;
  gap:10px;
}

@media(max-width:520px){
  .gami-wrap{
    gap:8px;
    justify-content:space-between;
  }
}

/* =======================================================
   COLUMNAS
   ======================================================= */
.streak-col,
.shop-col,
.perk-col{
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:flex-start;
  gap:6px;
  min-width:unset;
  width:86px;
  position:relative;
}

@media(max-width:520px){
  .streak-col,
  .shop-col,
  .perk-col{
    width:82px;
  }
}

/* =======================================================
   BOTONES
   ======================================================= */
.streak-btn,
.shop-btn,
.perk-btn{
  background:transparent;
  border:0;
  padding:0;
  cursor:pointer;
  display:flex;
  align-items:center;
  justify-content:center;
  border-radius:16px;
  outline:none;
}

.streak-btn:focus-visible{ box-shadow:0 0 0 3px rgba(37,99,235,.4); }
.shop-btn:focus-visible{ box-shadow:0 0 0 3px rgba(37,99,235,.35); }
.perk-btn:focus-visible{ box-shadow:0 0 0 3px rgba(147,51,234,.35); }

/* =======================================================
   RACHA (modo curso) - compacto
   ======================================================= */
.streak-circle-wrap{
  position:relative;
  width:72px;
  height:72px;
  border-radius:50%;
  flex-shrink:0;
  display:flex;
  align-items:center;
  justify-content:center;
  box-shadow:0 10px 22px rgba(0,0,0,.07);
  transition:transform .12s ease, box-shadow .12s ease, filter .12s ease;
  background:transparent;
}
/* ✅ Racha activa (reclamada): SIN animación, solo color estático */
.streak-circle-wrap.streak-active{ 
  animation: none; 
  box-shadow: 0 10px 22px rgba(0,0,0,.07), 0 0 10px rgba(255,122,24,.40);
}

/* ✅ Puede reclamar (hizo tarea pero no ha reclamado): CON animación */
.streak-circle-wrap.streak-can-claim{ 
  animation: ringGlow 1.6s ease-in-out infinite; 
}
.streak-circle-wrap.streak-dim{ filter:grayscale(.22) brightness(.93); box-shadow:0 10px 22px rgba(0,0,0,.07), 0 0 10px rgba(0,0,0,.06); }
.streak-circle-wrap:hover{ transform:scale(1.05); box-shadow:0 14px 26px rgba(0,0,0,.10); }

.streak-ring-svg{
  position:absolute; inset:0;
  width:72px; height:72px;
  pointer-events:none;
  transform:rotate(-90deg);
  transform-origin:50% 50%;
  filter:drop-shadow(0 0 3px rgba(0,0,0,.14));
}

.ring-track{ fill:none; stroke:var(--ring-track); stroke-width:8; stroke-linecap:round; }
.ring-progress{ fill:none; stroke:#ff7a18; stroke-width:8; stroke-linecap:round; transition:none; filter:drop-shadow(0 0 5px rgba(0,0,0,.22)); }

.streak-ignite-ring{ animation: igniteBurst .9s cubic-bezier(.16,1,.3,1); }
.streak-ignite-ring::after{
  content:"";
  position:absolute; inset:-10px;
  border-radius:999px;
  background:radial-gradient(circle at 50% 50%, rgba(255,184,77,.55) 0%, rgba(255,122,24,0.0) 70%);
  animation: igniteFlash .9s ease-out forwards;
  pointer-events:none;
  filter:blur(6px);
  z-index:-1;
}

.streak-circle-inner{
  position:relative;
  width:56px;
  height:56px;
  border-radius:50%;
  background:#fff;
  border:2px solid rgba(0,0,0,.04);
  box-shadow:0 8px 16px rgba(0,0,0,.07), inset 0 1px 2px rgba(0,0,0,.08);
  display:flex;
  align-items:center;
  justify-content:center;
  line-height:1;
  font-feature-settings:'tnum';
  font-weight:800;
  letter-spacing:-.03em;
  font-size:1rem;
  color:#0b1b36;
  transition:color .12s ease;
  z-index:2;
  text-align:center;
}
.streak-circle-inner.dim-num{ color:#6b7280; }

.streak-inner-stack{ display:flex; flex-direction:column; align-items:center; gap:2px; }
.streak-flame-icon{ width:20px; height:20px; display:flex; align-items:center; justify-content:center; }
/* ✅ Fuego activo (reclamado): SIN animación pulsante */
.streak-flame-on{ 
  width:20px; 
  height:20px; 
  color:#ff7a18; 
  filter:drop-shadow(0 4px 6px rgba(255,122,24,.38)); 
  animation: none; /* ✅ quitamos la animación por defecto */
}

/* ✅ Fuego cuando puede reclamar: CON animación pulsante */
.streak-circle-wrap.streak-can-claim .streak-flame-on{ 
  animation: flamePulse 1.4s ease-in-out infinite; 
}.streak-flame-dim{ width:20px; height:20px; color:#9ca3af; filter:drop-shadow(0 3px 4px rgba(0,0,0,.22)); opacity:.85; }

.streak-ignite-flame{ animation: igniteFlamePop .7s cubic-bezier(.16,1,.3,1); transform-origin:center bottom; }
.streak-num{ position:relative; z-index:2; font-size:.95rem; font-weight:900; }
.streak-ignite-num{ animation: igniteNumPop .7s cubic-bezier(.16,1,.3,1); }

/* Badges */
.streak-status-badge,
.shop-status-badge,
.perk-status-badge{
  display:inline-flex;
  align-items:center;
  justify-content:center;
  height:22px;
  min-width:68px;
  padding:0 8px;
  font-size:.72rem;
  font-weight:800;
  line-height:1;
  border-radius:999px;
  white-space:nowrap;
}

.streak-status-safe{
  border:1px solid rgba(16,185,129,.35);
  background:linear-gradient(135deg,#ecfdf5,#f0fff4);
  color:#065f46;
  box-shadow:0 8px 16px rgba(16,185,129,.16);
}

/* ✅ ANTES: animaba SIEMPRE. Ahora NO anima por defecto */
.streak-status-claim{
  border:1px solid rgba(244,63,94,.35);
  background:#fff1f2;
  color:#be123c;
  box-shadow:0 8px 16px rgba(244,63,94,.14);
}

/* ✅ Solo se anima cuando tú lo pidas */
.streak-status-claim.pulse{
  animation: badgePulse 1.3s infinite;
}

/* ✅ Igual para el modo urgente */
.streak-status-urgent{
  box-shadow:0 0 10px rgba(244,63,94,.45), 0 10px 18px rgba(0,0,0,.16);
}
.streak-status-urgent.pulse{
  animation: badgeUrgent .7s infinite;
}

/* =======================================================
   TIENDA (común)
   ======================================================= */
.shop-circle-wrap{
  position:relative;
  width:72px;
  height:72px;
  border-radius:50%;
  display:flex;
  align-items:center;
  justify-content:center;
  box-shadow:0 10px 22px rgba(0,0,0,.07);
  transition:transform .12s ease, box-shadow .12s ease, filter .12s ease;
  background:transparent;
}



.shop-circle-wrap:hover{ transform:scale(1.05); box-shadow:0 14px 26px rgba(0,0,0,.10); }

.shop-circle-wrap.shop-active{
  animation:none;
  box-shadow:0 10px 22px rgba(0,0,0,.07);
  border:1px solid rgba(15, 95, 184, .14);
}
.shop-circle-wrap.shop-dim{ filter:grayscale(.12) brightness(.99); }

.shop-ring-svg{
  position:absolute; inset:0;
  width:72px; height:72px;
  transform:rotate(-90deg);
  transform-origin:50% 50%;
  filter:drop-shadow(0 0 3px rgba(0,0,0,.14));
}
.shop-track{ fill:none; stroke:var(--ring-track); stroke-width:8; stroke-linecap:round; }
.shop-progress{ fill:none; stroke:url(#shopGrad); stroke-width:8; stroke-linecap:round; filter:drop-shadow(0 0 5px rgba(0,0,0,.20)); }

.shop-circle-inner{
  position:relative;
  width:56px;
  height:56px;
  border-radius:50%;
  background:#fff;
  border:2px solid rgba(0,0,0,.04);
  box-shadow:0 8px 16px rgba(0,0,0,.07), inset 0 1px 2px rgba(0,0,0,.08);
  display:flex;
  align-items:center;
  justify-content:center;
}

.shop-inner-stack{ 
  display:flex; 
  flex-direction:column; 
  align-items:center; 
  justify-content:center; /* ✅ centra verticalmente */
  line-height:1.05; 
  gap:2px; 
  width:100%; /* ✅ ocupa todo el ancho disponible */
}
.shop-coin-icon{ 
  width:20px; 
  height:20px; 
  color:#f59e0b;
  margin:0 auto; /* ✅ centra horizontalmente el ícono */
}
.perk-amount{ 
  font-weight:900; 
  font-size:.95rem; 
  color:#0b1b36;
  text-align:center;
}
.shop-status-badge{
  border:1px solid #bfdbfe;
  background:#eff6ff;
  color:#1d4ed8;
  box-shadow:0 8px 16px rgba(59,130,246,.12);
}

/* =======================================================
   COMODINES (modo componente)
   ======================================================= */
.perk-circle-wrap{
  position:relative;
  width:72px;
  height:72px;
  border-radius:50%;
  display:flex;
  align-items:center;
  justify-content:center;
  box-shadow:0 10px 22px rgba(0,0,0,.07);
  transition:transform .12s ease, box-shadow .12s ease, filter .12s ease;
  background:transparent;
}
.perk-circle-wrap:hover{ transform:scale(1.05); box-shadow:0 14px 26px rgba(0,0,0,.10); }

.perk-circle-wrap.perk-active{
  animation:none;
  box-shadow:0 10px 22px rgba(0,0,0,.07);
  border:1px solid rgba(147, 51, 234, .14);
}
.perk-circle-wrap.perk-dim{ filter:grayscale(.08) brightness(.99); }

.perk-ring-svg{
  position:absolute; inset:0;
  width:72px; height:72px;
  transform:rotate(-90deg);
  transform-origin:50% 50%;
  filter:drop-shadow(0 0 3px rgba(0,0,0,.14));
}

.perk-track{ fill:none; stroke:var(--ring-track); stroke-width:8; stroke-linecap:round; }
.perk-progress{ fill:none; stroke:url(#perkGrad); stroke-width:8; stroke-linecap:round; filter:none; }

.perk-circle-inner{
  position:relative;
  width:56px;
  height:56px;
  border-radius:50%;
  background:#fff;
  border:2px solid rgba(0,0,0,.04);
  box-shadow:0 8px 16px rgba(0,0,0,.07), inset 0 1px 2px rgba(0,0,0,.08);
  display:flex;
  align-items:center;
  justify-content:center;
}
.perk-inner-stack{ 
  display:flex; 
  flex-direction:column; 
  align-items:center; 
  justify-content:center; /* ✅ centra verticalmente */
  line-height:1.05; 
  gap:2px; 
  width:100%; /* ✅ ocupa todo el ancho */
}
.perk-amount{ 
  font-weight:900; 
  font-size:.95rem; 
  color:#0b1b36;
  text-align:center; /* ✅ centra el texto */
}
.perk-status-badge{
  border:1px solid #e9d5ff;
  background:#faf5ff;
  color:#6b21a8;
  box-shadow:0 8px 16px rgba(147,51,234,.12);
}

/* ===== Animaciones ===== */
@keyframes ringGlow{
  0%{ box-shadow:0 10px 22px rgba(0,0,0,.07), 0 0 10px rgba(255,122,24,.40), 0 0 26px rgba(255,179,28,.22); }
  60%{ box-shadow:0 12px 24px rgba(0,0,0,.09), 0 0 16px rgba(255,122,24,.62), 0 0 34px rgba(255,179,28,.30); }
  100%{ box-shadow:0 10px 22px rgba(0,0,0,.07), 0 0 10px rgba(255,122,24,.40), 0 0 26px rgba(255,179,28,.22); }
}
@keyframes igniteBurst{
  0%{ transform:scale(.4) rotate(-12deg); filter:brightness(1.35); box-shadow:0 0 18px rgba(255,122,24,.8), 0 0 54px rgba(255,184,77,.5), 0 18px 34px rgba(0,0,0,.32); }
  40%{ transform:scale(1.16) rotate(4deg); filter:brightness(1.15); box-shadow:0 0 14px rgba(255,122,24,.62), 0 0 38px rgba(255,184,77,.35), 0 14px 26px rgba(0,0,0,.24); }
  70%{ transform:scale(.98) rotate(0deg); filter:brightness(1); }
  100%{ transform:scale(1) rotate(0deg); filter:brightness(1); }
}
@keyframes igniteFlash{ 0%{opacity:1; transform:scale(.4);} 40%{opacity:.9; transform:scale(1.35);} 100%{opacity:0; transform:scale(1.7);} }
@keyframes igniteNumPop{ 0%{ transform:scale(.3) translateY(8px); filter:brightness(1.35); } 40%{ transform:scale(1.18) translateY(-4px); filter:brightness(1.12); } 70%{ transform:scale(.94) translateY(0); filter:brightness(1); } 100%{ transform:scale(1) translateY(0); filter:brightness(1); } }
@keyframes igniteFlamePop{ 0%{ transform:scale(.3) translateY(8px); } 40%{ transform:scale(1.2) translateY(-3px); } 70%{ transform:scale(.92) translateY(0); } 100%{ transform:scale(1) translateY(0); } }
@keyframes flamePulse{ 0%{transform:scale(1);} 60%{transform:scale(1.08);} 100%{transform:scale(1);} }
@keyframes badgePulse{ 0%{box-shadow:0 0 0 0 rgba(244,63,94,.26);} 70%{box-shadow:0 0 0 10px rgba(244,63,94,0);} 100%{box-shadow:0 0 0 0 rgba(244,63,94,0);} }
@keyframes badgeUrgent{ 0%{transform:scale(1);} 50%{transform:scale(1.08);} 100%{transform:scale(1);} }

/* Botones de racha/tienda redondos (evita el cuadrado) */
.gami-statBtn{
  border-radius: 999px !important;
  overflow: hidden;
}

/* Círculo del contador centrado */
.gami-statCircle{
  display: grid !important;
  place-items: center !important;
}

/* Si el contenido se va a la izquierda, fuerza centrado */
.gami-statCircle *{
  margin: 0 auto;
}

.tour-col{
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  width: fit-content;
}

`;

/* inyecta el CSS una sola vez */
function injectStylesOnce() {
  if (typeof document === "undefined") return;
  if (document.getElementById("gami-stats-styles")) return;
  const s = document.createElement("style");
  s.id = "gami-stats-styles";
  s.textContent = CSS;
  document.head.appendChild(s);
}

/* ===== Helpers de racha ===== */
const normalizeStreak = (rawIn) => {
  const r = rawIn?.racha ?? rawIn ?? {};
  const current = Number(r.current ?? r.racha_actual_dias ?? 0);
  const best = Number(r.best ?? r.mejor_racha_dias ?? 0);

  const lastClaimYMD =
    r.lastClaimYMD ??
    r.last_claim_ymd ??
    (r.fecha_ultima_conexion
      ? new Date(r.fecha_ultima_conexion).toISOString().slice(0, 10)
      : null);

  const claimedToday = r.claimedToday ?? lastClaimYMD === getEcuadorYMD();

  // ✅ Importante: elegible si hizo tarea o test hoy (backend)
  // ✅ Señales (si backend las manda, SON la verdad)
  const didTaskRaw = r.didTaskToday ?? r.did_task_today;
  const didTestRaw = r.didLevelTestToday ?? r.did_level_test_today;

  const didTaskToday = didTaskRaw !== undefined ? !!didTaskRaw : false;
  const didLevelTestToday = didTestRaw !== undefined ? !!didTestRaw : false;

  // ✅ elegibleToday:
  // - si backend manda didTask/didTest -> usamos eso (evita eligibleToday=true "fantasma")
  // - si NO manda esas señales -> caemos al eligibleToday del backend
  const hasSignals = didTaskRaw !== undefined || didTestRaw !== undefined;
  const rawEligible = !!(r.eligibleToday ?? r.eligible_today);
  const eligibleToday = hasSignals ? (didTaskToday || didLevelTestToday) : rawEligible;

  return { current, best, lastClaimYMD, claimedToday, eligibleToday, didTaskToday, didLevelTestToday };

};


/* Utils */
const toNumber = (v, d = 0) => {
  const n = typeof v === "string" ? Number.parseFloat(v) : Number(v);
  return Number.isFinite(n) ? n : d;
};

export default function GamificationStats({
  variant = "course", // "course" | "component"
  componentId, // requerido si variant="component"
  refreshKey = 0, // ✅ AGREGAR ESTA LÍNEA
  coins,
  streak,
  onCoinsClick,
  onStreakClick,
  streakAttention,
  className = "",
  ariaLabelCoins = "Ir a Store",
  ariaLabelStreak = "Abrir racha diaria",
  streakGoalDays = 100,
}) {
  const {
    utmCoins,
    studentStreak,
    initializeStudentData,
    getStudentStreak,
    getComodinesState,
  } = useFuncion();

  // ---------- HOOKS (siempre arriba) ----------
  const [stylesInjected, setStylesInjected] = useState(false);

  // Racha (course)
  const [animPct, setAnimPct] = useState(0);
  const prevPctRef = useRef(0);
  const [ignite, setIgnite] = useState(false);
  const prevClaimedRef = useRef(false);
  const [isLate, setIsLate] = useState(false);

  // Tienda (común)
  const [igniteShop, setIgniteShop] = useState(false);
  const prevCoinsRef = useRef(0);

  // Comodines (component)
  const [perks, setPerks] = useState({ available: 0, max: 5 });
  const prevPerksRef = useRef({ available: 0, max: 5 });
  const [ignitePerk, setIgnitePerk] = useState(false);

  // Inyecta CSS una vez
  useEffect(() => {
    if (!stylesInjected) {
      injectStylesOnce();
      setStylesInjected(true);
    }
  }, [stylesInjected]);

  // Carga inicial
  useEffect(() => {
    initializeStudentData?.();
    getStudentStreak?.().catch(() => { });
  }, [initializeStudentData, getStudentStreak]);

  // Carga de comodines por componente
  useEffect(() => {
    let cancel = false;
    const fetchPerks = async () => {
      if (variant !== "component" || !componentId || !getComodinesState) return;
      try {
        let res;
        try {
          res = await getComodinesState({ componenteId: componentId });
        } catch {
          res = await getComodinesState(componentId);
        }
        const normalized = {
          available: Number(res?.comodines_restantes ?? res?.saldo ?? 0),
          max: Number(res?.max ?? 3),
        };
        if (!cancel) setPerks(normalized);
      } catch {
        if (!cancel) setPerks((p) => ({ ...p, max: p.max ?? 5 }));
      }
    };
    fetchPerks();
    return () => {
      cancel = true;
    };
  }, [variant, componentId, getComodinesState, refreshKey]);

  // ---------- Datos derivados ----------
  const coinsCtx = toNumber(utmCoins?.cantidad_actual, 0);
  const baseCoins =
    coins !== undefined
      ? typeof coins === "object"
        ? toNumber(coins?.cantidad_actual, 0)
        : toNumber(coins, 0)
      : coinsCtx;

  const streakCtx = useMemo(() => normalizeStreak(studentStreak), [studentStreak]);
  const streakNum =
    streak !== undefined ? toNumber(streak, 0) : toNumber(streakCtx?.current, 0);
  const claimedToday = !!streakCtx?.claimedToday;
  const needClaimToday = !claimedToday;
  const autoPulse = needClaimToday && !!streakCtx?.eligibleToday;


  // Urgencia
  useEffect(() => {
    const checkLate = () => setIsLate(new Date().getHours() >= 20);
    checkLate();
    const id = setInterval(checkLate, 60 * 1000);
    return () => clearInterval(id);
  }, []);
  const urgent = needClaimToday && isLate && !!streakCtx?.eligibleToday;
  const showBadge =
    typeof streakAttention === "boolean"
      ? streakAttention || urgent
      : needClaimToday || urgent || claimedToday;

  // ---------- Animación anillo racha ----------
  const RADIUS = 28;
  const CIRC = 2 * Math.PI * RADIUS;
  const pctRaw = streakGoalDays > 0 ? streakNum / streakGoalDays : 0;
  const pct = Math.max(0, Math.min(1, pctRaw));

  useEffect(() => {
    const start = prevPctRef.current;
    const end = pct;
    const t0 = performance.now();
    let raf;
    const step = (now) => {
      const p = Math.min(1, (now - t0) / 800);
      const eased = start + (end - start) * (1 - Math.pow(1 - p, 3));
      setAnimPct(eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    prevPctRef.current = end;
    return () => cancelAnimationFrame(raf);
  }, [pct]);

  const dashArray = CIRC;
  const dashOffset = CIRC * (1 - animPct);
  const progressStroke = needClaimToday ? "url(#streakDimGrad)" : "url(#streakGrad)";

  // Ignite al reclamar hoy
  useEffect(() => {
    if (claimedToday && !prevClaimedRef.current) {
      setIgnite(true);
      const t = setTimeout(() => setIgnite(false), 1000);
      prevClaimedRef.current = claimedToday;
      return () => clearTimeout(t);
    }
    prevClaimedRef.current = claimedToday;
  }, [claimedToday]);

  // Efecto tienda cuando suben monedas
  useEffect(() => {
    if (baseCoins > prevCoinsRef.current) {
      setIgniteShop(true);
      const t = setTimeout(() => setIgniteShop(false), 800);
      prevCoinsRef.current = baseCoins;
      return () => clearTimeout(t);
    }
    prevCoinsRef.current = baseCoins;
  }, [baseCoins]);

  // Ignite comodines
  useEffect(() => {
    const inc = (perks.available ?? 0) > (prevPerksRef.current?.available ?? 0);
    if (inc) {
      setIgnitePerk(true);
      const t = setTimeout(() => setIgnitePerk(false), 900);
      prevPerksRef.current = perks;
      return () => clearTimeout(t);
    }
    prevPerksRef.current = perks;
  }, [perks]);

  // Accesibilidad teclado
  const onKey = (cb) => (e) => {
    if (!cb) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      cb();
    }
  };

  const StatusBadge = () => {
    if (!showBadge) return null;

    if (claimedToday) {
      return <span className="streak-status-badge streak-status-safe">RACHA</span>;
    }


    const pulse = autoPulse;


    return (

      <button
        type="button"
        className={
          "streak-status-badge streak-status-claim " +
          (urgent ? "streak-status-urgent " : "") +
          (pulse ? "pulse" : "")
        }
        aria-label="Reclamar racha diaria"
        title="Reclamar racha"
      >
        RACHA
      </button>

    );
  };

  if (variant === "component") {
    const totalAvailable = Math.max(0, Number(perks.available ?? 0));

   return (
      <div className="flex flex-col gap-2" style={{ width: "100%", height: "100%" }}>

        {/* ── Monedas ── */}
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-xl"
          style={{ background: "#fff", border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,.05)", width: "100%", flex: 1 }}
          data-tour="coins-card"
        >
          <Coins className="w-4 h-4 flex-shrink-0" style={{ color: "#f59e0b" }} />
          <span className="text-base font-black text-gray-800 leading-none">{baseCoins}</span>
          <span
            className="text-[0.65rem] font-extrabold tracking-widest uppercase ml-auto"
            style={{ color: "#f59e0b" }}
          >
            UTMCOINS
          </span>
        </div>

        {/* ── Comodines ── */}
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-xl"
          style={{ background: "#fff", border: "1px solid #e2e8f0", boxShadow: "0 1px 4px rgba(0,0,0,.05)", width: "100%", flex: 1 }}
          data-tour="comodin-card"
        >
          <Zap className="w-4 h-4 flex-shrink-0" style={{ color: "#9333ea" }} />
          <span className="text-base font-black text-gray-800 leading-none">{totalAvailable}</span>
          <span
            className="text-[0.65rem] font-extrabold tracking-widest uppercase ml-auto"
            style={{ color: "#9333ea" }}
          >
            COMODINES
          </span>
          <button
            onClick={(e) => { e.stopPropagation(); onCoinsClick?.(); }}
            aria-label="Comprar comodín"
            className="w-6 h-6 rounded-full flex items-center justify-center border flex-shrink-0"
            style={{ background: "#faf5ff", borderColor: "#d8b4fe", color: "#9333ea" }}
          >
            <Plus size={11} strokeWidth={3} />
          </button>
        </div>

      </div>
    );
  }

  // ---------- Variante por defecto: "course" (racha + tienda) ----------

  // ---------- Variante por defecto: "course" (racha + tienda) ----------
  return (
    <div className={`gami-wrap ${className}`}>
      {/* ✅ Racha CON data-tour */}
      <div className="streak-col" data-tour="streak-card">
        <button
          type="button"
          className="streak-btn"
          onClick={onStreakClick}
          onKeyDown={onKey(onStreakClick)}
          aria-label={ariaLabelStreak}
          title="Racha diaria"
        >
          <div
            className={
              "streak-circle-wrap " +
              (claimedToday ? "streak-active" : "streak-dim") +
              (ignite ? " streak-ignite-ring" : "") +
              (autoPulse ? " streak-can-claim" : "") 
            }
          >
            <svg className="streak-ring-svg" viewBox="0 0 80 80" width="72" height="72">
              <defs>
                <linearGradient id="streakGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#ff7a18" />
                  <stop offset="60%" stopColor="#ffb31c" />
                  <stop offset="100%" stopColor="#ffd56a" />
                </linearGradient>
                <linearGradient id="streakDimGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#9ca3af" />
                  <stop offset="60%" stopColor="#d1d5db" />
                  <stop offset="100%" stopColor="#e5e7eb" />
                </linearGradient>
              </defs>
              <circle className="ring-track" cx="40" cy="40" r={RADIUS} strokeWidth="8" strokeLinecap="round" />
              <circle
                className="ring-progress"
                cx="40"
                cy="40"
                r={RADIUS}
                strokeWidth="8"
                strokeLinecap="round"
                style={{ strokeDasharray: dashArray, strokeDashoffset: dashOffset, stroke: progressStroke }}
              />
            </svg>

            <div className={"streak-circle-inner " + (needClaimToday ? "dim-num " : "") + (ignite ? "streak-ignite-num" : "")}>
              <div className="streak-inner-stack">
                <div className="streak-flame-icon">
                  <Flame className={(needClaimToday ? "streak-flame-dim" : "streak-flame-on") + (ignite ? " streak-ignite-flame" : "")} />
                </div>
                <span className="streak-num">{streakNum}</span>
              </div>
            </div>
          </div>
        </button>

        <StatusBadge />
      </div>

      {/* ✅ Tienda CON data-tour */}
      <div className="shop-col" data-tour="coins-card">
        <button
          type="button"
          className="shop-btn"
          onClick={onCoinsClick}
          onKeyDown={onKey(onCoinsClick)}
          aria-label={ariaLabelCoins}
          title="Abrir Tienda"
        >
          <div className={"shop-circle-wrap " + (baseCoins > 0 ? "shop-active" : "shop-dim")}>
            <svg className="shop-ring-svg" viewBox="0 0 80 80" width="72" height="72">
              <defs>
                <linearGradient id="shopGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#0f5fb8" />
                  <stop offset="60%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#a5b4fc" />
                </linearGradient>
              </defs>
              <circle className="shop-track" cx="40" cy="40" r={RADIUS} strokeWidth="8" strokeLinecap="round" />
              <circle className="shop-progress" cx="40" cy="40" r={RADIUS} strokeWidth="8" strokeLinecap="round" />
            </svg>

            <div className="shop-circle-inner">
              <div className="shop-inner-stack">
                <Coins className={"shop-coin-icon" + (igniteShop ? " shop-ignite-bag" : "")} />
                <span className="shop-amount">{baseCoins}</span>
              </div>
            </div>
          </div>
        </button>

        <span className="shop-status-badge">UTMCOINS</span>
      </div>
    </div>
  );

}